import _ from 'lodash';

// ---------------------------------------------------------------------------

const FLEX_VALUE_TO_CSS = {
  flexStart: 'flex-start',
  flexEnd: 'flex-end',
  spaceBetween: 'space-between',
  spaceAround: 'space-around',
  spaceEvenly: 'space-evenly',
  rowReverse: 'row-reverse',
  columnReverse: 'column-reverse',
  wrapReverse: 'wrap-reverse',
};

export function flexValueToCss(value) {
  return _.get(FLEX_VALUE_TO_CSS, value, value);
}

export function pxToNumber(value) {
  if (_.isNumber(value)) return value;
  return parseInt(value, 10) || 0;
}

export function numberToPx(value) {
  return `${pxToNumber(value)}px`;
}

export function resolvePadding(padding) {
  if (_.isNumber(padding) || _.isString(padding)) {
    const v = pxToNumber(padding);
    return { topLeft: v, topRight: v, bottomRight: v, bottomLeft: v, lock: true };
  }
  return {
    topLeft: pxToNumber(_.get(padding, 'topLeft', 0)),
    topRight: pxToNumber(_.get(padding, 'topRight', 0)),
    bottomRight: pxToNumber(_.get(padding, 'bottomRight', 0)),
    bottomLeft: pxToNumber(_.get(padding, 'bottomLeft', 0)),
    lock: _.get(padding, 'lock', false),
  };
}

export function paddingToStored(sidePadding) {
  return {
    topLeft: numberToPx(sidePadding.topLeft),
    topRight: numberToPx(sidePadding.topRight),
    bottomRight: numberToPx(sidePadding.bottomRight),
    bottomLeft: numberToPx(sidePadding.bottomLeft),
    lock: sidePadding.lock,
  };
}

export function paddingToCss(padding) {
  const p = resolvePadding(padding);
  return `${p.topLeft}px ${p.topRight}px ${p.bottomRight}px ${p.bottomLeft}px`;
}

export const MOBILE_MAX_PADDING = 20;

export function capPaddingSides(padding, max = MOBILE_MAX_PADDING) {
  const p = resolvePadding(padding);
  return {
    topLeft: Math.min(p.topLeft, max),
    topRight: Math.min(p.topRight, max),
    bottomRight: Math.min(p.bottomRight, max),
    bottomLeft: Math.min(p.bottomLeft, max),
    lock: p.lock,
  };
}

export const LAYOUT_TYPE_FLEX = 'flex';
export const LAYOUT_TYPE_FREEFORM = 'freeform';

// layoutType is tri-state: 'flex' | 'freeform' | null (null = inherit the
// parent's behaviour).
//
// Pages saved before layoutType existed only carry the boolean flexBoxEnabled,
// so the rule is: if layoutType has a value, it decides; otherwise fall back to
// flexBoxEnabled (true -> 'flex', false -> 'freeform', missing -> null).
// "Has a value" means non-null -- the API sends layoutType: null on widgets
// that predate it, and that must not hide their flexBoxEnabled.
export function normalizeLayoutType(value) {
  if (value === LAYOUT_TYPE_FLEX || value === LAYOUT_TYPE_FREEFORM) return value;
  if (value === true) return LAYOUT_TYPE_FLEX;
  if (value === false) return LAYOUT_TYPE_FREEFORM;
  return null;
}

function hasLayoutType(props) {
  return _.get(props, 'layoutType') != null;
}

function storedLayoutType(widgetProps, breakpoint) {
  const props = _.get(widgetProps, ['properties', breakpoint]);
  if (!props) return null;
  return normalizeLayoutType(hasLayoutType(props) ? props.layoutType : props.flexBoxEnabled);
}

// The widget's own explicit mode, before any parent inheritance is applied.
export function ownLayoutType(widgetProps, breakpoint) {
  const desktop = storedLayoutType(widgetProps, 'desktop');
  if (breakpoint !== 'mobile') return desktop;

  const mobileProps = _.get(widgetProps, 'properties.mobile') || {};
  const overridden = mobileProps.flexModeOverridden === true;

  if (hasLayoutType(mobileProps)) {
    const mobile = normalizeLayoutType(mobileProps.layoutType);
    if (overridden || mobile === LAYOUT_TYPE_FLEX) return mobile;
    return mobile === null ? desktop : mobile;
  }

  // Legacy mobile boolean keeps its original rule: it only counts when the
  // mobile mode was overridden or it is true; otherwise mobile follows desktop.
  const legacy = mobileProps.flexBoxEnabled;
  if (overridden) return legacy ? LAYOUT_TYPE_FLEX : LAYOUT_TYPE_FREEFORM;
  if (legacy === true) return LAYOUT_TYPE_FLEX;
  return desktop;
}

// parentFlex is the parent's already-resolved boolean. Callers inside the widget
// tree read it from FlexFlowContext; callers outside it resolve the ancestor
// chain themselves. Defaults to false so a widget with no parent info and no
// explicit mode stays freeform, matching the old default.
export function resolveFlexEnabled(widgetProps, breakpoint, parentFlex = false) {
  const own = ownLayoutType(widgetProps, breakpoint);
  if (own === null) return !!parentFlex;
  return own === LAYOUT_TYPE_FLEX;
}
